import { useGame } from "~/app";
import { Choice, EffectChoiceResult } from "~/business";
import { commandBuilderFactory } from "~/business/commandBuilderFactory";
import {
  XAgentEffect,
  XButton,
  XPackBy,
  XPlayerIcon,
  XTypography,
} from "~/jsx/components/design";
import { ModalNames } from "~/jsx/context/ModalNames";
import { Spacing } from "~/jsx/theme";
import { isJsonEqual } from "~/utils";
import { XDesignModal } from "../design/XDesignModal";
import { XChoice } from "./XChoice";
import { useViewingPlayerOpt } from "~/jsx/hook";

export const XChoiceModal = () => {
  const viewingPlayer = useViewingPlayerOpt();
  const { game, update } = useGame();
  const { executor } = game;
  const effectContext = executor.getEffectContext();
  if (!viewingPlayer) return null;
  const choice = executor.getNextChoiceForPlayer(viewingPlayer.id);

  if (!choice || !effectContext) return null;

  const {
    possibleChoices = [],
    type,
    optional,
    id: choiceId,
    playerId,
    results,
  } = choice;
  const choosingPlayer = game.getPlayer(playerId);
  const isChoosingPlayer = choosingPlayer.id === viewingPlayer?.id;

  const visible =
    Boolean(choice) && isChoosingPlayer && !Choice.isAnswered(choice);
  const commandBuilderObj = commandBuilderFactory();

  const answer = (choiceResult: EffectChoiceResult) => {
    if (!choiceId || !playerId) {
      return;
    }
    update(commandBuilderObj.answerChoice(choiceResult));
  };

  const onChoice = (value: any) => {
    if (
      !isChoosingPlayer ||
      results.find((result) => !result.skip && isJsonEqual(value, result.value))
    ) {
      return;
    }
    return () => {
      answer({ skip: false, value });
    };
  };
  const onSkip = isChoosingPlayer
    ? () => {
        answer({ skip: true });
      }
    : undefined;

  return (
    <XDesignModal
      name={ModalNames.choice}
      visible={visible}
      canHide={!isChoosingPlayer}
      title={
        <>
          {isChoosingPlayer ? (
            <>Please choose</>
          ) : (
            <div style={{ display: "flex", alignItems: "center" }}>
              <XPlayerIcon width={32} playerId={choosingPlayer.id} />{" "}
              {choosingPlayer.name} chooses
            </div>
          )}{" "}
          ({type})
        </>
      }
    >
      <XTypography>Current effect:</XTypography>
      <div
        style={{
          width: "fit-content",
          padding: 5,
          borderRadius: 3,
          marginBottom: 10,
          background:
            "linear-gradient(180deg, #9E9893 0%, #A19589 19.79%, #A39385 50%, #A29489 79.17%, #9E9893 100%)",
        }}
      >
        <XAgentEffect
          effectDescription={effectContext}
          width={32}
          context="choice"
        />
      </div>

      <XPackBy packSize={6} gap={10}>
        {possibleChoices.map((value, index) => (
          <XButton
            key={index}
            onClick={onChoice(value)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <XChoice value={value} playerId={playerId} choiceType={type} />
          </XButton>
        ))}
      </XPackBy>
      {optional && (
        <div style={{ width: "fit-content" }}>
          <XTypography style={{ textAlign: "center", margin: Spacing.px24 }}>
            OR
          </XTypography>
          <XButton onClick={onSkip} variant="action">
            Skip action
          </XButton>
        </div>
      )}
    </XDesignModal>
  );
};
